import { useState, useEffect, useCallback, useMemo } from 'react';
import { FiFilter, FiSearch, FiInbox, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiRefreshCw, FiCalendar, FiPlus } from 'react-icons/fi';
import TicketCard from '../components/TicketCard';
import { fetchTickets } from '../api/api';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import './TicketsList.css';

const normalizeTicket = (t) => ({
  id: t.id,
  subject: t.title,
  title: t.title,
  description: t.description,
  status: (t.status || 'open').replace('-', ' '),
  priority: (t.priority || 'medium').toLowerCase(),
  customerName: t.created_by_name || t.createdBy || 'Customer',
  assigned_to_name: t.assigned_to_name,
  assignedToName: t.assigned_to_name,
  createdAt: t.created_at || t.createdAt,
  replies: 0,
});

const TicketsList = ({ userRole }) => {
  const { user } = useAuth();
  const socket = useSocket();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [ticketIdSearch, setTicketIdSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const ticketsPerPage = 10;

  const loadTickets = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterStatus !== 'all') params.status = filterStatus.replace(' ', '-');
    fetchTickets(params)
      .then((data) => setTickets(Array.isArray(data) ? data : data?.data || []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [filterStatus]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (!socket) return;
    const onUpdate = () => loadTickets();
    socket.on('ticket:created', onUpdate);
    socket.on('ticket:assigned', onUpdate);
    socket.on('ticket:updated', onUpdate);
    return () => {
      socket.off('ticket:created', onUpdate);
      socket.off('ticket:assigned', onUpdate);
      socket.off('ticket:updated', onUpdate);
    };
  }, [socket, loadTickets]);

  const normalized = tickets.map(normalizeTicket);

  // Analytics stats
  const stats = useMemo(() => {
    const total = normalized.length;
    const open = normalized.filter(t => t.status.toLowerCase() === 'open').length;
    const inProgress = normalized.filter(t => t.status.toLowerCase() === 'in progress' || t.status.toLowerCase() === 'in-progress').length;
    const resolved = normalized.filter(t => t.status.toLowerCase() === 'resolved').length;
    const closed = normalized.filter(t => t.status.toLowerCase() === 'closed').length;
    const highPriority = normalized.filter(t => t.priority.toLowerCase() === 'high').length;
    return { total, open, inProgress, resolved, closed, highPriority };
  }, [normalized]);

  const filteredTickets = normalized.filter((ticket) => {
    const matchesSearch =
      (ticket.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === 'all' || ticket.status.toLowerCase().replace(' ', '-') === filterStatus.toLowerCase().replace(' ', '-');
    const matchesCategory =
      filterCategory === 'all' || !ticket.category || (ticket.category && ticket.category.toLowerCase() === filterCategory.toLowerCase());
    const matchesTicketId =
      !ticketIdSearch || String(ticket.id).toLowerCase().includes(ticketIdSearch.toLowerCase());
    let matchesDate = true;
    const created = ticket.createdAt ? new Date(ticket.createdAt) : null;
    if (created && filterStartDate) matchesDate = created >= new Date(filterStartDate);
    if (matchesDate && created && filterEndDate) matchesDate = created <= new Date(filterEndDate);
    return matchesSearch && matchesStatus && matchesCategory && matchesTicketId && matchesDate;
  });

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.created_at || 0).getTime();
    const dateB = new Date(b.createdAt || b.created_at || 0).getTime();
    return sortBy === 'latest' ? dateB - dateA : dateA - dateB;
  });
  const totalPages = Math.max(1, Math.ceil(sortedTickets.length / ticketsPerPage));
  const paginatedTickets = sortedTickets.slice((currentPage - 1) * ticketsPerPage, currentPage * ticketsPerPage);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <FiAlertCircle />;
      case 'in-progress': return <FiClock />;
      case 'resolved': return <FiCheckCircle />;
      case 'closed': return <FiXCircle />;
      default: return <FiInbox />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#F44336';
      case 'in-progress': return '#FF9800';
      case 'resolved': return '#4CAF50';
      case 'closed': return '#9E9E9E';
      default: return '#757575';
    }
  };

  return (
    <div className="tickets-list">
      {/* Header */}
      <div className="tickets-header">
        <div className="tickets-header-title">
          <h1>{userRole === 'admin' ? 'All Tickets' : userRole === 'engineer' ? 'Assigned Tickets' : 'My Tickets'}</h1>
          <span className="tickets-subtitle">{stats.total} total tickets • {stats.open} open • {stats.inProgress} in progress</span>
        </div>
        <button className="tickets-refresh-btn" onClick={loadTickets} disabled={loading}>
          <FiRefreshCw className={loading ? 'spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="tickets-stats">
        <div className="stat-card total">
          <div className="stat-icon"><FiInbox /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
        <div className="stat-card open">
          <div className="stat-icon"><FiAlertCircle /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.open}</span>
            <span className="stat-label">Open</span>
          </div>
        </div>
        <div className="stat-card progress">
          <div className="stat-icon"><FiClock /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.inProgress}</span>
            <span className="stat-label">In Progress</span>
          </div>
        </div>
        <div className="stat-card resolved">
          <div className="stat-icon"><FiCheckCircle /></div>
          <div className="stat-info">
            <span className="stat-value">{stats.resolved}</span>
            <span className="stat-label">Resolved</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="tickets-filter-bar">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by title, description, or ticket ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              ×
            </button>
          )}
        </div>

        {/* Status Filter Buttons */}
        <div className="status-filters">
          {[
            { key: 'all', label: 'All', count: stats.total },
            { key: 'open', label: 'Open', count: stats.open },
            { key: 'in-progress', label: 'In Progress', count: stats.inProgress },
            { key: 'resolved', label: 'Resolved', count: stats.resolved },
            { key: 'closed', label: 'Closed', count: stats.closed },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              className={`status-btn ${filterStatus === key ? 'active' : ''}`}
              onClick={() => setFilterStatus(key)}
            >
              {getStatusIcon(key)}
              <span>{label}</span>
              {count > 0 && <span className="status-count">{count}</span>}
            </button>
          ))}
        </div>

        {/* Additional Filters */}
        <div className="additional-filters">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
            <option value="latest">Latest First</option>
            <option value="oldest">Oldest First</option>
          </select>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="filter-select">
            <option value="all">All Categories</option>
            <option value="technical">Technical</option>
            <option value="payment">Payment</option>
            <option value="account">Account</option>
            <option value="parking">Parking</option>
            <option value="other">Other</option>
          </select>
          <div className="date-range">
            <FiCalendar className="calendar-icon" />
            <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
            <span>to</span>
            <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="tickets-results-bar">
        <span className="results-text">
          {loading ? 'Loading tickets...' : `Showing ${paginatedTickets.length} of ${filteredTickets.length} tickets`}
        </span>
        {!loading && filteredTickets.length > 0 && (
          <span className="results-page">Page {currentPage} of {totalPages}</span>
        )}
      </div>

      {/* Tickets Grid */}
      <div className="tickets-grid">
        {loading ? (
          <div className="tickets-loading">
            <div className="loading-spinner"></div>
            <p>Loading tickets...</p>
          </div>
        ) : paginatedTickets.length > 0 ? (
          paginatedTickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} userRole={userRole || user?.role} />
          ))
        ) : (
          <div className="tickets-empty">
            <div className="empty-icon"><FiInbox /></div>
            <h3>No tickets found</h3>
            <p>{searchTerm ? 'Try adjusting your search or filters' : 'No tickets match your criteria'}</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} 
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ← Previous
          </button>
          <span className="pagination-info">Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} 
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default TicketsList;
